import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import useDebounce from "../../hooks/useDebounce";
import AppBar from "../../common/AppBar";
import AppTable from "../../common/AppTable/AppTable";
import Loader from "../../common/Loader";
import Snackbar from "../../common/Snackbar";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrashCan } from "@fortawesome/free-solid-svg-icons";
import {
  deleteCanvasPageTemplate,
  fetchAllCanvasPageTemplates,
  exportCanvasTemplatesCSV,
  setLimit,
  setOffset,
} from "../../redux/canvasTemplatesSlice";
import { buildPermissionContext } from "../../util/permissions";

const columnNames = [
  {
    headName: "Canvas Page Name",
    field: "canvas_page_template_name",
    sort: true,
    filter: true,
    width: "35%",
  },
  {
    headName: "Description",
    field: "canvas_page_template_description",
    sort: true,
    filter: true,
    width: "45%",
  },
  {
    headName: "Category",
    field: "category_name",
    sort: true,
    filter: true,
    width: "20%",
  },
  {
    headName: "Action",
    field: "action",
    isAction:true,
  },
];

const CanvasTemplates = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { templates, loading, pagination } = useSelector(
    (state) => state.canvasTemplates
  );
  const authUser = useSelector((state) => state.auth.user);
  const permissionCtx = buildPermissionContext(authUser);
  const canEdit = permissionCtx.permissions?.Edit === true;

  // Initialize local state from localStorage to avoid flash of wrong values
  const [limit, setLocalLimit] = useState(() => {
    const saved = localStorage.getItem("appTableLimit");
    return saved ? parseInt(saved) : 10;
  });
  const [offset, setLocalOffset] = useState(0); // Always start at page 1
  const [searchQuery, setSearchQuery] = useState("");
  const [currentApiQuery, setCurrentApiQuery] = useState("");
  const [localLoading, setLocalLoading] = useState(false);
  const tableContainerRef = useRef();
  const isUpdatingRef = useRef(false);
  const debouncedSearchQuery = useDebounce(searchQuery, 300);
  const [isActionLoading, setIsActionLoading] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [isPaginationInitialized, setIsPaginationInitialized] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    type: "",
  });
  const showSnackbar = (message, type) => {
    setSnackbar({ open: true, message, type });
  };
  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  // Memoized search parameters to prevent unnecessary re-renders
  const searchParams = useMemo(
    () => ({
      limit,
      offset,
      searchQuery: currentApiQuery || debouncedSearchQuery || "",
      hasSearch: !!(currentApiQuery || debouncedSearchQuery),
    }),
    [limit, offset, currentApiQuery, debouncedSearchQuery]
  );

  // Create reduxSlice object for AppTable - optimized version
  const reduxSlice = useMemo(
    () => ({
      setLimit: (value) => {
        // Update Redux first, then local state
        dispatch(setLimit(value));
        setLocalLimit(value);
        // Reset offset to 0 when limit changes
        dispatch(setOffset(0));
        setLocalOffset(0);
        // Also update localStorage to stay in sync
        localStorage.setItem("appTableLimit", value.toString());
        localStorage.setItem("appTableOffset", "0");
      },
      setOffset: (value) => {
        // Update Redux first, then local state
        dispatch(setOffset(value));
        setLocalOffset(value);
        // Also update localStorage to stay in sync
        localStorage.setItem("appTableOffset", value.toString());
      },
    }),
    [dispatch]
  );

  const handleFilterSort = useCallback(
    (queryString) => {
      // Remove limit and offset from AppTable's query string
      // We'll manage those ourselves through Redux
      const params = new URLSearchParams(queryString);
      params.delete("limit");
      params.delete("offset");
      const cleanedQuery = params.toString();

      // Only update if the query actually changed
      if (cleanedQuery !== searchQuery) {
        setSearchQuery(cleanedQuery);
        setCurrentApiQuery(cleanedQuery);
      }
    },
    [searchQuery]
  );

  // Sync localStorage to Redux and local state on mount - RUN FIRST
  useEffect(() => {
    const savedLimit = localStorage.getItem("appTableLimit");

    // Preserve user's limit preference, but always reset to page 1 on mount
    const limitValue = savedLimit ? parseInt(savedLimit) : 10;
    const offsetValue = 0; // Always reset to page 1 on page load/refresh

    // CRITICAL: Clear localStorage offset FIRST to prevent any race conditions
    localStorage.setItem("appTableOffset", "0");

    // CRITICAL: Force Redux state update synchronously
    dispatch(setOffset(0)); // Reset offset FIRST
    dispatch(setLimit(limitValue)); // Then set limit
    setLocalLimit(limitValue);
    setLocalOffset(0); // Force to 0

    // Mark pagination as initialized AFTER Redux dispatch
    // Use setTimeout to ensure Redux state update completes
    setTimeout(() => {
      setIsPaginationInitialized(true);
    }, 0);

    // Cleanup: Reset Redux state when component unmounts
    return () => {
      dispatch(setOffset(0));
    };
  }, [dispatch]); // Run only once on mount

  // Keep local state in sync with Redux pagination state
  useEffect(() => {
    if (pagination?.limit !== undefined && pagination.limit !== limit) {
      setLocalLimit(pagination.limit);
    }
    if (pagination?.offset !== undefined && pagination.offset !== offset) {
      setLocalOffset(pagination.offset);
    }
  }, [pagination?.limit, pagination?.offset, limit, offset]);

  // Consolidated API call function with proper error handling and deduplication
  const fetchTemplates = useCallback(
    async (apiParams) => {
      if (isUpdatingRef.current) {
        return;
      }

      isUpdatingRef.current = true;
      setLocalLoading(true);

      try {
        const result = await dispatch(
          fetchAllCanvasPageTemplates({
            searchQuery: apiParams.searchQuery || "",
            limit: apiParams.limit,
            offset: apiParams.offset,
          })
        );
        return result;
      } finally {
        isUpdatingRef.current = false;
        setLocalLoading(false);
      }
    },
    [dispatch]
  );

  // Separate useEffect for search changes to prevent pagination conflicts
  useEffect(() => {
    // Only fetch when search query changes, not on pagination
    if (!searchParams.hasSearch) {
      return;
    }

    // Skip if this is initial load
    if (isInitialLoad) {
      return;
    }

    // Use Redux pagination limit as source of truth
    const currentLimit = pagination?.limit || 10;

    const apiParams = {
      limit: currentLimit,
      offset: 0, // Always reset to first page when search changes
      searchQuery: searchParams.searchQuery,
    };

    // Reset offset when search changes
    dispatch(setOffset(0));
    setLocalOffset(0);

    // Use a small delay to batch rapid changes
    const timeoutId = setTimeout(() => {
      fetchTemplates(apiParams);
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [
    searchParams.hasSearch,
    searchParams.searchQuery,
    pagination?.limit,
    fetchTemplates,
    isInitialLoad,
    dispatch,
  ]);

  // Separate useEffect for pagination changes
  useEffect(() => {
    // CRITICAL: Don't run at all until BOTH conditions are met
    // This prevents running with stale Redux values during initialization
    if (!isPaginationInitialized || isInitialLoad) {
      return;
    }

    // ALWAYS use Redux pagination values as source of truth
    if (pagination?.limit === undefined) {
      return; // Wait for pagination to be initialized
    }

    const apiParams = {
      limit: pagination.limit,
      offset: pagination.offset || 0,
      searchQuery: searchParams.searchQuery || "", // Use current search query (if any)
    };

    // Use a small delay to batch rapid changes
    const timeoutId = setTimeout(() => {
      fetchTemplates(apiParams);
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [
    pagination?.limit,
    pagination?.offset,
    searchParams.searchQuery,
    fetchTemplates,
    isInitialLoad,
    isPaginationInitialized,
  ]);

  // Initial data load effect - waits for Redux state to be synced
  useEffect(() => {
    // Wait for pagination to be initialized from localStorage
    if (!isPaginationInitialized) {
      return;
    }

    // Skip if returning from AddCanvasTemplate (handled by separate effect)
    if (location.state?.fromAddEdit) {
      setIsInitialLoad(false); // Mark as loaded so we don't fetch twice
      return;
    }

    // Wait for pagination state to be initialized from localStorage
    if (pagination?.limit === undefined) {
      return; // Wait for first useEffect to sync localStorage to Redux
    }

    // Only run once on initial load
    if (!isInitialLoad) {
      return;
    }

    // CRITICAL: Always use offset 0 for initial load, ignore any stale Redux values
    const apiParams = {
      limit: pagination.limit,
      offset: 0, // Force page 1 on initial load
      searchQuery: "",
    };

    fetchTemplates(apiParams);
    setIsInitialLoad(false);
  }, [
    pagination?.limit,
    isInitialLoad,
    fetchTemplates,
    location.state?.fromAddEdit,
    isPaginationInitialized,
  ]); // Removed pagination?.offset dependency since we force it to 0

  // Handle returning from AddCanvasTemplate - reset to first page but preserve limit
  useEffect(() => {
    if (location.state?.fromAddEdit) {
      // Wait for pagination to be initialized from localStorage first
      if (!isPaginationInitialized) {
        return;
      }

      if (pagination?.limit === undefined) {
        return;
      }

      // Read directly from localStorage as source of truth, fallback to Redux
      const savedLimit = localStorage.getItem("appTableLimit");
      const preservedLimit = savedLimit
        ? parseInt(savedLimit)
        : pagination.limit;
      const resetOffset = 0;

      // Update localStorage to ensure consistency
      localStorage.setItem("appTableLimit", preservedLimit.toString());
      localStorage.setItem("appTableOffset", resetOffset.toString());

      // Update Redux state
      dispatch(setOffset(resetOffset));
      dispatch(setLimit(preservedLimit));

      // Update local state
      setLocalOffset(resetOffset);
      setLocalLimit(preservedLimit);

      // Clear the fromAddEdit flag to prevent re-triggering
      window.history.replaceState({}, document.title);

      // Fetch data with preserved limit
      const apiParams = {
        limit: preservedLimit,
        offset: resetOffset,
        searchQuery: currentApiQuery || "",
      };

      // Small delay to ensure state updates propagate
      setTimeout(() => {
        fetchTemplates(apiParams);
      }, 50);
    }
  }, [
    location.state?.fromAddEdit,
    dispatch,
    currentApiQuery,
    fetchTemplates,
    pagination?.limit,
    isPaginationInitialized,
  ]);

  const handleCreateClick = () => {
    localStorage.removeItem("short_description");
    localStorage.removeItem("canvas_name");
    localStorage.removeItem("canvas_category_name");
    localStorage.removeItem("canvas_category_id");
    localStorage.removeItem("canvasSnapshot");
    localStorage.removeItem("snapshot");
    localStorage.removeItem("canvasPages");
    window.indexedDB.deleteDatabase("canvasElementsDB");

    navigate("/add-canvas-template", {
      state: {
        editMode: false,
        returnSearchQuery: currentApiQuery, // Pass current search state
      },
    });
  };

  const handleDelete = async (templateId) => {
    try {
      setIsActionLoading(true);
      const result = await dispatch(deleteCanvasPageTemplate(templateId));

      // Check if deletion was successful
      if (result.meta.requestStatus === "fulfilled") {
        showSnackbar("Canvas template deleted successfully.", "success");
        // After successful deletion, refresh the current search to show remaining records
        // Use the current API query that was last used
        const queryToUse = currentApiQuery || "";

        // If we're on a page with only one item and we delete it, go to previous page
        const currentPageItemCount = templates?.length || 0;
        const currentLimit = pagination?.limit || limit;
        const currentOffset = pagination?.offset || offset;
        const shouldGoToPreviousPage =
          currentPageItemCount === 1 && currentOffset > 0;
        let effectiveOffset = currentOffset;
        if (shouldGoToPreviousPage) {
          const newOffset = Math.max(0, currentOffset - currentLimit);
          effectiveOffset = newOffset;
          dispatch(setOffset(newOffset));
          setLocalOffset(newOffset);
        }

        // Refresh the data with the current query
        const refreshResult = await fetchTemplates({
          searchQuery: queryToUse,
          limit: currentLimit,
          offset: effectiveOffset,
        });

        // Check if the refresh returned no results
        if (
          refreshResult.meta.requestStatus === "fulfilled" &&
          (!refreshResult.payload?.results ||
            refreshResult.payload.results.length === 0)
        ) {
          await fetchTemplates({
            searchQuery: "",
            limit: currentLimit,
            offset: effectiveOffset,
          });
        }
      } else {
        showSnackbar("Failed to delete canvas template.", "error");
      }
    } catch (e) {
      showSnackbar("An error occurred while deleting.", "error");
    } finally {
      setIsActionLoading(false);
    }
  };

  const handleExportCSV = async () => {
    await dispatch(exportCanvasTemplatesCSV());
  };

  const contentAfterHeader = (
    <div
      style={{ textAlign: "right", marginTop: "-30px", marginBottom: "8px" }}
    >
      <button
        className="appbar-button"
        onClick={canEdit ? handleCreateClick : undefined}
        disabled={!canEdit}
        style={{
          opacity: canEdit ? 1 : 0.5,
          cursor: canEdit ? "pointer" : "not-allowed",
        }}
      >
        Add New Canvas Page Template
      </button>
    </div>
  );

  const paginationLeftContent = (
    <button
      className="appbar-buttonpopup"
      style={{
        border: "1px solid #0D6EFD",
        backgroundColor: "#ffffff",
        color: "#0D6EFD",
        height: "40px",
        fontSize: "13px",
        marginLeft: "-1px",
        marginTop: "10px",
        opacity: canEdit ? 1 : 0.5,
        cursor: canEdit ? "pointer" : "not-allowed",
      }}
      onClick={() => {
        if (!canEdit) return;
        handleExportCSV();
      }}
      disabled={!canEdit}
    >
      Export CSV
    </button>
  );

  // Compute correct pagination metadata to avoid stale data issues
  const computedPagination = useMemo(() => {
    if (!pagination) {
      return { total: 0, totalPages: 1, limit: 10, offset: 0 };
    }

    // If pagination hasn't been initialized yet, use local state (always offset 0)
    if (!isPaginationInitialized) {
      return {
        total: 0,
        totalPages: 1,
        limit: limit,
        offset: 0, // CRITICAL: Always 0 until initialized
      };
    }

    // ALWAYS use Redux pagination values as source of truth AFTER initialization
    const currentLimit = pagination.limit;
    const currentOffset = pagination.offset;
    const total = pagination.total || pagination.count || 0;

    // Recalculate totalPages based on current limit to avoid stale data
    const totalPages = currentLimit > 0 ? Math.ceil(total / currentLimit) : 1;

    return {
      ...pagination,
      limit: currentLimit,
      offset: currentOffset,
      total,
      totalPages,
    };
  }, [pagination, isPaginationInitialized, limit, offset]);

  return (
    <div>
      <AppBar
        title="Canvas Page Templates"
        buttons={[]}
        showTablesDropdown={false}
        showBlackTitle={true}
      />

      <AppTable
        ref={tableContainerRef}
        columnsData={columnNames}
        showSearchInput={true}
        paginate={true}
        pagination={{
          limit: [computedPagination.limit, setLocalLimit],
          offset: [computedPagination.offset, setLocalOffset],
          pagination: computedPagination,
          paginationPosition: {
            position: "right",
            leftContent: paginationLeftContent,
          },
        }}
        extrapadding="0px"
        onFilterSortHandler={handleFilterSort}
        contentAfterHeader={contentAfterHeader}
        reduxSlice={reduxSlice}
        enableLocalStoragePersistence={true}
      >
        {loading || localLoading || !templates ? (
          <tr>
            <td colSpan="4" style={{ textAlign: "center", padding: "20px" }}>
              Loading...
            </td>
          </tr>
        ) : templates.length > 0 ? (
          templates.map((template) => (
            <tr key={template.canvas_page_template_id}>
              <td>{template.canvas_page_template_name || ""}</td>
              <td>{template.canvas_page_template_description}</td>
              <td>{template.category_details?.category_name || ""}</td>
              <td>
                <div className="app-action-btns">
                  <button
                    className="edit-btn"
                    onClick={() => {
                      if (!canEdit) return;
                      localStorage.removeItem("canvasSnapshot");
                      localStorage.removeItem("snapshot");
                      localStorage.removeItem("canvasPages");
                      localStorage.removeItem("fromMyCanvas");
                      localStorage.setItem(
                        "canvas_category_id",
                        template.canvas_page_category
                      );
                      localStorage.setItem(
                        "canvas_category_name",
                        template.category_details?.category_name || ""
                      );
                      localStorage.setItem(
                        "canvas_name",
                        template.canvas_page_template_name
                      );
                      localStorage.setItem(
                        "short_description",
                        template.canvas_page_template_description
                      );
                      window.indexedDB.deleteDatabase("canvasElementsDB");

                      navigate("/add-canvas-template", {
                        state: {
                          editMode: true,
                          template,
                          returnSearchQuery: currentApiQuery, // Pass current search state
                        },
                      });
                    }}
                    disabled={!canEdit}
                    style={{
                      opacity: canEdit ? 1 : 0.5,
                      cursor: canEdit ? "pointer" : "not-allowed",
                    }}
                  >
                    {/* <FontAwesomeIcon icon={faPencil} /> */}
                    <svg
                      width="20"
                      height="21"
                      viewBox="0 0 15 16"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M13.3782 5.57534L9.89 2.12329L11.039 0.972603C11.3537 0.657534 11.7401 0.5 12.1983 0.5C12.6566 0.5 13.043 0.657534 13.3577 0.972603L14.5067 2.12329C14.8213 2.43836 14.9855 2.81849 14.9991 3.2637C15.0128 3.7089 14.8624 4.08904 14.5477 4.40411L13.3782 5.57534ZM12.1881 6.78767L3.48817 15.5H0V12.0068L8.69991 3.29452L12.1881 6.78767Z"
                        fill="white"
                      />
                    </svg>
                  </button>
                  <button
                    className="delete-btn"
                    onClick={() =>
                      handleDelete(template.canvas_page_template_id)
                    }
                    disabled={!canEdit}
                    style={{
                      opacity: canEdit ? 1 : 0.5,
                      cursor: canEdit ? "pointer" : "not-allowed",
                    }}
                  >
                    <FontAwesomeIcon icon={faTrashCan} />
                  </button>
                </div>
              </td>
            </tr>
          ))
        ) : (
          <tr>
            <td colSpan="4" style={{ textAlign: "center", padding: "20px" }}>
              No records found
            </td>
          </tr>
        )}
      </AppTable>
      {isActionLoading && <Loader text="Processing..." />}
      {snackbar.open && (
        <Snackbar
          message={snackbar.message}
          type={snackbar.type}
          onClose={handleCloseSnackbar}
        />
      )}
    </div>
  );
};

export default CanvasTemplates;
